import { format } from "date-fns";

import { getHtmlLikeText } from "components/word-processor-field/word-processor-value.utils";
import type { CurrentUser } from "features/auth/auth-slice";
import type {
  StaffTask,
  StaffTaskInput
} from "services/types/staff-tasks.type";
import { convertHtmlToText, formatBackendDateOnly } from "utils/common";

import { Patient } from "../../services/types/patients.type";

export type StaffTaskV2FormValues = {
  id?: number;
  title: string;
  priority?: string;
  status?: string | number;
  description: string;
  assignee: { label: string; value: string } | null;
  assignees: { label: string; value: string }[];
  due_at: string | null;
  patient: Patient | null;
};

export const EMPTY_DEFAULT_TASK_VALUES: StaffTaskV2FormValues = {
  title: "",
  priority: "0",
  description: "",
  status: "",
  assignees: [],
  assignee: null,
  due_at: "",
  patient: null
};

export const formatStaffTaskToInput = (data: StaffTaskV2FormValues) => {
  const result: StaffTaskInput = {
    id: data.id,
    title: data.title,
    description: convertHtmlToText(data.description),
    priority: Number(data.priority) || 0,
    status: data.status ? Number(data.status) : undefined,
    assignee: data.assignee?.value ? Number(data.assignee?.value) : null,
    assignees: data.assignees
      .filter((assignee) => assignee.value !== data.assignee?.value)
      .map((assignee) => ({
        user: Number(assignee.value)
      })),
    due_at: data.due_at
      ? format(new Date(data.due_at), "yyyy-MM-dd'T'HH:mm:ss")
      : null,
    patient: data.patient?.id ? Number(data.patient?.id) : null
  };

  return result;
};

export const getInitialValues = (
  editMode: StaffTask | undefined,
  currentUser: CurrentUser
): StaffTaskV2FormValues => {
  if (editMode) {
    return {
      id: editMode?.id,
      priority: String(editMode?.priority) || "",
      title: editMode?.title ?? "",
      status: editMode.status,
      description: getHtmlLikeText(editMode?.description ?? ""),
      due_at: editMode?.due_at
        ? formatBackendDateOnly(editMode?.due_at, "MM/dd/yyyy")
        : "",
      assignee: editMode?.assignee
        ? {
            label: String(editMode?.assignee?.fullName),
            value: String(editMode?.assignee.id)
          }
        : null,
      assignees: editMode?.assignees
        ? editMode?.assignees.map((assignee) => ({
            label: String(assignee.user?.fullName),
            value: String(assignee.user?.id)
          }))
        : [],
      patient: editMode?.patient
    };
  }

  return {
    title: "",
    priority: "0",
    description: "",
    due_at: null,
    assignee: { label: currentUser.fullName, value: String(currentUser.id) },
    assignees: [],
    patient: null
  };
};

export const getCardPosition = (index: number, count: number) => {
  if (count === 1) {
    return "single";
  } else if (index === 0) {
    return "first";
  } else if (index === count - 1) {
    return "last";
  }

  return undefined;
};
